<template>
  <div class="flex flex-col h-full overflow-y-hidden bg-white mx-10 mt-5 p-5">
    <div class="border-b pb-5 flex items-center w-full">
      <h1 class="text-2xl font-bold">Questions</h1>
      <a class="text-blue-500 hover:text-blue-700 cursor-pointer ml-auto" @click="newQuestion">New Question</a>
    </div>
    <div class="flex flex-1 h-full overflow-y-hidden pt-5">
      <div class="w-[30%] max-w-[450px] min-w-[250px] mr-5 h-full overflow-y-scroll p-5 border">
        <ItemContainer v-for="question in questions" :key="question.id" @click="setCurrentQuestion(question)">
          {{ question.title }}
        </ItemContainer>
      </div>
      <div class="grow border p-5 h-full overflow-y-scroll">
        <FormKit
          v-if="currentQuestion"
          :config="{
            classes: {
              inner: 'max-w-screen-md',
            },
          }"
          type="form"
          v-model="currentQuestion"
          @submit="handleSubmit"
        >
          <div v-if="currentQuestion.id" class="flex justify-end">
            <LSButton
              class="flex justify-end"
              variant="outline"
              type="danger"
              size="small"
              @click.prevent.stop="removeQuestion(currentQuestion.title, currentQuestion.id)"
            >
              <template #icon>
                <TrashIcon />
              </template>
            </LSButton>
          </div>
          <FormKit type="text" name="title" label="Title" validation="required" inner-class="max-w-screen-md" />
          <FormKit type="text" name="code" label="Code" validation="required" />
          <FormKit type="text" name="placeholder" label="Placeholder" />
          <FormKit type="textarea" name="info" label="Info" />
          <FormKit type="select" name="template_variable_id" label="Template Variable" :options="templateVariableOptions" />
          <FormKit type="select" name="depending_template_variable_id" label="Depends on" :options="dependingTemplateVariableOptions" />
          <FormKit
            type="select"
            name="validator"
            label="Validator"
            validation="required"
            :options="validatorOptions"
            v-if="dependsOnTemplateVariable"
          />
          <FormKit
            v-if="currentQuestion.validator === 'oneofstring'"
            type="checkbox"
            name="values"
            label="Values"
            :options="oneOfStringOptions"
            validation="required"
          />
          <FormKit
            v-if="currentQuestion.validator === 'eq' || currentQuestion.validator === 'neq'"
            type="text"
            name="value"
            label="Value"
            validation="required"
          />
          <FormKit
            alt-label-position
            off-value-label="Editing enabled"
            on-value-label="Editing disabled"
            type="toggle"
            name="display_only"
            label="Display Only"
          />
        </FormKit>
        <div v-else>
          <div class="">Select a question or <a class="cursor-pointer underline" @click="newQuestion">create a new one</a></div>
        </div>
      </div>
    </div>
  </div>
  <DialogModal
    :show="dialogData.isOpen"
    :title="dialogData.title"
    :description="dialogData.description"
    :action-buttons="[
      { onClick: dialogData.accept, type: 'danger', label: 'Delete' },
      { onClick: dialogData.cancel, type: 'secondary', label: 'Cancel' },
    ]"
  >
  </DialogModal>
</template>

<script setup lang="ts">
import type { ApiQuestion } from '@/admin/types'
import { useAdminStore } from '../../../admin/adminStore'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import ItemContainer from './ItemContainer.vue'
import DialogModal from '@/components/DialogModal.vue'
import LSButton from '@/components/LSButton.vue'
import TrashIcon from '@/assets/icons/24/trash.svg?component'

const adminStore = useAdminStore()
const { templateVariables, questions } = storeToRefs(adminStore)

const currentQuestion = ref<ApiQuestion | undefined>(undefined)
const selectedQuestionStr = ref('')

const dialogData = ref({
  isOpen: false,
  description: '',
  title: '',
  accept: () => {},
  cancel: () => {
    dialogData.value.isOpen = false
  },
})

const closeModal = () => {
  dialogData.value = {
    isOpen: false,
    description: '',
    title: '',
    accept: () => {},
    cancel: () => {
      dialogData.value.isOpen = false
    },
  }
}

const newQuestion = () => {
  currentQuestion.value = {} as ApiQuestion
  selectedQuestionStr.value = 'newQuestion'
}

const setCurrentQuestion = (question: ApiQuestion) => {
  currentQuestion.value = { ...question }
  selectedQuestionStr.value = question?.code
}

const removeQuestion = (questionTitle: string, id: string) => {
  dialogData.value.isOpen = true
  dialogData.value.title = 'Remove Question'
  dialogData.value.description = `would you like to remove the question "${questionTitle}" ?`
  dialogData.value.accept = () => {
    adminStore.removeQuestion(id)
    closeModal()
  }
}

const oneOfStringOptions = computed(() => {
  const input_type = dependsOnTemplateVariable.value?.input_type
  if (!input_type) return []

  switch (input_type) {
    case 'singleselect':
    case 'multiselect':
      return dependsOnTemplateVariable.value.select_options.map(selectOption => {
        return {
          label: selectOption.label,
          value: selectOption.code,
        }
      })
    default:
      console.error(`one of string should not be possible for ${input_type}`)
      return []
  }
})

const dependsOnTemplateVariable = computed(() => {
  const dependsOnId = currentQuestion?.value?.depending_template_variable_id
  if (dependsOnId) {
    return templateVariables.value.find(v => v.id === dependsOnId)
  }
  return undefined
})

const validatorOptions = computed(() => {
  const input_type = dependsOnTemplateVariable.value?.input_type
  if (!input_type) return []

  let options = [
    { label: '-', value: null },
    { label: 'Filled', value: 'filled' },
  ]

  switch (input_type) {
    case 'singleselect':
    case 'multiselect':
      options = [
        ...options,
        { label: 'One of string', value: 'oneofstring' },
        { label: 'Equal', value: 'eq' },
        { label: 'Not Equal', value: 'neq' },
      ]
      break
    case 'text':
      options = [...options, { label: 'Equal', value: 'eq' }, { label: 'Not Equal', value: 'neq' }]
      break
    default:
      break
  }

  return options
})

const templateVariableOptions = computed(() => {
  return templateVariables.value.map(variable => ({
    label: variable.label,
    value: variable.id,
  }))
})

const dependingTemplateVariableOptions = computed(() => {
  return [{ label: 'None', value: null }, ...templateVariableOptions.value]
})

const handleSubmit = (data: ApiQuestion) => {
  selectedQuestionStr.value = data.code
  return adminStore.saveQuestion(data)
}

watch(
  () => questions.value,
  () => updateQuestionFormStore()
)

const updateQuestionFormStore = () => {
  const activeQuestion = questions.value?.find(({ code }) => code === selectedQuestionStr.value)

  if (activeQuestion) {
    setCurrentQuestion(activeQuestion)
  } else {
    selectedQuestionStr.value = ''
  }
}
</script>
